import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getDeletedItemsSummary, listDeletedItems, restoreDeletedItem,
  purgeDeletedItem, exportDeletedItems,
} from '../api';
import { useAuth } from '../contexts/AuthContext';
import { formatDateDdMmmYy, formatRupeesUI } from '../utils/format';
import { showToast } from '../utils/toast';

interface EntityMeta { label: string; numberField: string; orderBy: string }
type SummaryShape = {
  counts: Record<string, number>;
  entities: Record<string, EntityMeta>;
};

const TAB_ORDER = ['client', 'task', 'subTask', 'certificate', 'compliance', 'invoice', 'creditNote', 'reimbursement'];

const DeletedItems: React.FC = () => {
  const { isAdmin } = useAuth();
  const [summary, setSummary] = useState<SummaryShape | null>(null);
  const [tab, setTab] = useState<string>('task');
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const refreshSummary = useCallback(async () => {
    try {
      const res = await getDeletedItemsSummary();
      setSummary(res.data);
    } catch (err: any) {
      showToast(err?.response?.data?.message ?? 'Failed to load summary', 'error');
    }
  }, []);

  const refreshRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listDeletedItems(tab);
      setRows(res.data.rows);
    } catch (err: any) {
      showToast(err?.response?.data?.message ?? 'Failed to load deleted items', 'error');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { refreshSummary(); }, [refreshSummary]);
  useEffect(() => { refreshRows(); }, [refreshRows]);

  const onRestore = async (id: number) => {
    if (!window.confirm('Restore this item? It will reappear in the main lists.')) return;
    try {
      await restoreDeletedItem(tab, id);
      showToast('Restored', 'success');
      await refreshSummary(); await refreshRows();
    } catch (err: any) {
      showToast(err?.response?.data?.message ?? 'Restore failed', 'error');
    }
  };

  const onPurge = async (id: number) => {
    if (!window.confirm(
      'Permanently delete this item? This cannot be undone — the row will be removed from the database entirely.'
    )) return;
    try {
      await purgeDeletedItem(tab, id);
      showToast('Permanently deleted', 'success');
      await refreshSummary(); await refreshRows();
    } catch (err: any) {
      showToast(err?.response?.data?.message ?? 'Purge failed', 'error');
    }
  };

  const onExport = async () => {
    try {
      const res = await exportDeletedItems(tab);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      const label = summary?.entities[tab]?.label ?? tab;
      a.href = url;
      a.download = `Deleted_${label.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      showToast('Failed to export', 'error');
    }
  };

  const numberField = useMemo(() => summary?.entities[tab]?.numberField ?? 'id', [summary, tab]);

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <div className="text-center">
          <div className="text-4xl mb-3">🔒</div>
          <p className="font-medium">Admin access required</p>
          <p className="text-sm mt-1">Only administrators can view deleted items.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-gray-900">Deleted Items</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          View, restore, permanently delete, or export anything that has been deleted from the app.
          Restoring a Task or Client also restores everything that was deleted together with it.
        </p>
      </div>

      {/* Entity tabs with counts */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex flex-wrap border-b border-gray-200 bg-gray-50">
          {TAB_ORDER.map((e) => {
            const meta = summary?.entities[e];
            const count = summary?.counts[e] ?? 0;
            return (
              <button
                key={e}
                onClick={() => setTab(e)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                  tab === e ? 'border-blue-600 text-blue-700 bg-white' : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <span>{meta?.label ?? e}</span>
                {count > 0 && (
                  <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">{count}</span>
                )}
              </button>
            );
          })}
        </div>

        <div className="p-4 flex items-center justify-between border-b border-gray-100">
          <div className="text-xs text-gray-500">
            {loading ? 'Loading…' : `${rows.length} deleted ${summary?.entities[tab]?.label ?? tab}${rows.length === 1 ? '' : 's'}`}
          </div>
          <button onClick={onExport} className="text-sm text-blue-600 hover:text-blue-800 underline">
            Export to Excel
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-3 py-2">Number / ID</th>
                <th className="text-left px-3 py-2">What</th>
                <th className="text-left px-3 py-2">Context</th>
                <th className="text-left px-3 py-2">Deleted</th>
                <th className="text-right px-3 py-2">Amount</th>
                <th className="text-right px-3 py-2 w-44">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading ? (
                <tr><td colSpan={6} className="px-3 py-12 text-center text-gray-400">
                  Nothing here. Deleted {summary?.entities[tab]?.label.toLowerCase() ?? tab}s will appear here.
                </td></tr>
              ) : rows.map((r: any) => <DeletedRow
                key={r.id} entity={tab} row={r} numberField={numberField}
                onRestore={() => onRestore(r.id)} onPurge={() => onPurge(r.id)}
              />)}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const DeletedRow: React.FC<{
  entity: string;
  row: any;
  numberField: string;
  onRestore: () => void;
  onPurge: () => void;
}> = ({ entity, row, numberField, onRestore, onPurge }) => {
  const number = row[numberField] ?? `#${row.id}`;
  // Friendly "what" + "context" depend on entity
  let what = '';
  let context = '';
  let amount: number | null = null;
  switch (entity) {
    case 'client':
      what = row.clientName ?? '';
      context = [row.pan, row.phone].filter(Boolean).join(' · ');
      break;
    case 'task':
      what = row.taskName ?? '';
      context = `${row.client?.clientName ?? ''}${row.partner ? ' · ' + row.partner.staffName : ''}`;
      break;
    case 'subTask':
      what = row.name ?? '';
      context = row.task ? `${row.task.taskId} ${row.task.taskName}` : '';
      break;
    case 'certificate':
      what = row.name ?? '';
      context = row.task ? `${row.task.taskId} ${row.task.taskName}` : '';
      break;
    case 'compliance':
      what = row.complianceType?.name ?? '';
      context = `${row.task?.taskId ?? ''} ${row.task?.taskName ?? ''} · ${row.period ?? ''}`.trim();
      break;
    case 'invoice':
      what = `Invoice (${row.status})`;
      context = row.client?.clientName ?? '';
      amount = Number(row.totalAmount ?? row.amount ?? 0);
      break;
    case 'creditNote':
      what = `Credit Note (${row.status})`;
      context = `${row.client?.clientName ?? ''}${row.invoice ? ' · for ' + row.invoice.invoiceNumber : ''}`;
      amount = Number(row.totalAmount ?? row.amount ?? 0);
      break;
    case 'reimbursement':
      what = `Claim (${row.status})`;
      context = `${row.staff?.staffName ?? ''}${row.task ? ' · ' + row.task.taskId : ''}`;
      break;
  }

  return (
    <tr className="border-b border-gray-50 hover:bg-gray-50/60 align-top">
      <td className="px-3 py-2 font-mono text-blue-700 whitespace-nowrap">{number}</td>
      <td className="px-3 py-2 text-gray-700 max-w-[260px]" title={what}>
        <div className="truncate">{what || '—'}</div>
      </td>
      <td className="px-3 py-2 text-gray-600 max-w-[260px]" title={context}>
        <div className="truncate text-xs">{context}</div>
      </td>
      <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
        {row.deletedAt ? formatDateDdMmmYy(row.deletedAt) : '—'}
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        {amount != null && amount > 0 ? `₹${formatRupeesUI(amount)}` : ''}
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        <button onClick={onRestore} className="text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 mr-1">
          Restore
        </button>
        <button onClick={onPurge} className="text-xs px-2 py-1 rounded bg-red-50 text-red-700 border border-red-200 hover:bg-red-100">
          Delete permanently
        </button>
      </td>
    </tr>
  );
};

export default DeletedItems;
